/** @file clip.setEvents: replaces the events of the key at one frame (creating or deleting the key). */
import type { CommandDef } from '../apply/commandDef';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import type { Command } from '../types/command';
import { readInput } from '../validate/readInput';
import { checkFrame } from './checkFrame';
import { keyAt } from './keyAt';
import { keyWarnings } from './keyWarnings';
import { readEvents } from './readEvents';
import { writeKey } from './writeKey';

/**
 * `clip.setEvents { clip, frame, events }` → `{}`: a key is created at a frame without one; `[]` on a key without
 * nodes deletes it. Inverse: the previous events, or deleting the created key.
 */
export const clipSetEventsCommand: CommandDef<'clip.setEvents'> = {
  name: 'clip.setEvents',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'frame', 'events']);
    return { clip: r.str('clip'), frame: r.int('frame', { min: 0 }), events: readEvents(r.raw('events'), r.at('events')) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    checkFrame(clip, input.frame, '/input/frame');
    const prev = keyAt(clip, input.frame);
    const inverse: Command[] = prev === undefined
      ? [{ name: 'clip.setKeys', input: { clip: name, keys: [{ frame: input.frame, nodes: {} }], merge: 'replace' } }]
      : [{ name: 'clip.setEvents', input: { clip: name, frame: input.frame, events: structuredClone(prev.events ?? []) } }];
    const key = { ...structuredClone(prev ?? { frame: input.frame, nodes: {} }), events: structuredClone(input.events) };
    writeKey(clip, key, 'replace');
    checkClip(name, clip);
    return { result: {}, warnings: keyWarnings(draft, name, clip, [key]), inverse };
  },
};
